import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Server } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
  StatusBadge,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  adminAccountsService,
  type AdminAccountRow,
} from '@/domains/hosting/services/adminAccountsService';
import { formatDate, formatMB, formatZAR, usagePercent, usageTone } from '@/shared/utils/format';
import { dunningPolicyMeta } from '@/domains/hosting/dunningPolicy';
import { hostingStateMeta, booksVsServerMeta, desiredReasonLabel } from '@/domains/hosting/hostingState';

/** '—' for an absent amount. The FORMATTING is the shared helper's job;
 *  only the null presentation is local. Named so it can never be mistaken
 *  for another local currency formatter. */
function zarOrDash(n: number | null): string {
  return n === null ? '—' : formatZAR(n);
}

export default function AdminAccountsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [serverFilter, setServerFilter] = useState<string>('all');
  // Slice 2 (2026-09-10): the one filter this page never had — "where does
  // the server disagree with the books?" On 2026-09-10 that was 10 of 13.
  const [disagreeOnly, setDisagreeOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminAccountsService
      .listAll(false)
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const servers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.whm_server))).sort(),
    [rows],
  );

  /**
   * Hosting accounts are a STOCK set — default `all-time` (Rule T-1).
   *
   * The label is `Linked`, not "Created": the row's date is `linked_at`, the
   * day the cPanel account was attached to a portal customer, which is NOT the
   * day the account was created on WHM. Calling it "Created" would be a
   * plausible, wrong answer to a question nobody asked (Rule T-2).
   */
  const table = useTableFilters<AdminAccountRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Linked',
    dateOf: (r) => r.linked_at,
    searchText: (r) =>
      [
        r.cpanel_username,
        r.primary_domain ?? '',
        r.customer_name ?? '',
        r.customer_email ?? '',
        r.package_name ?? '',
      ].join(' '),
    facets: useMemo(
      () => [
        (r: AdminAccountRow) => serverFilter === 'all' || r.whm_server === serverFilter,
        (r: AdminAccountRow) =>
          !disagreeOnly || booksVsServerMeta(r.books_vs_server).disagree,
      ],
      [serverFilter, disagreeOnly],
    ),
  });

  const filtered = table.rows;

  const columns = useMemo<UniversalTableColumn<AdminAccountRow>[]>(
    () => [
      {
        key: 'username',
        header: 'cPanel user',
        sortValue: (r) => r.cpanel_username,
        render: (r) => (
          <div className="flex flex-col">
            <code className="text-sm text-foreground">{r.cpanel_username}</code>
            {r.primary_domain && (
              <span className="text-xs text-muted-foreground">{r.primary_domain}</span>
            )}
          </div>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.customer_name ?? '—'}</span>
            {r.customer_email && (
              <span className="text-xs text-muted-foreground">{r.customer_email}</span>
            )}
          </div>
        ),
      },
      {
        key: 'plan',
        header: 'Plan',
        sortValue: (r) => r.service_plan_name ?? '',
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.service_plan_name ?? '(no plan)'}</span>
            {r.service_plan_amount !== null && (
              <span className="text-xs text-muted-foreground">
                {zarOrDash(r.service_plan_amount)} / {r.billing_cycle}
              </span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
      {
        key: 'whm-package',
        header: 'WHM package',
        sortValue: (r) => r.package_name ?? '',
        render: (r) => (
          <code className="text-xs text-muted-foreground">
            {r.package_name ?? '—'}
          </code>
        ),
        hideOnMobile: true,
      },
      {
        // F-18. The list named the package but never how full the account was.
        // "Basic" tells you the ceiling; only this tells you whether the plan
        // still fits, which is the question that decides an upsell or a support
        // call. Source is the daily `usage_snapshot` rollup, so the reading has
        // a date and the column says which.
        key: 'disk',
        header: 'Disk used',
        sortValue: (r) =>
          r.disk_used_mb !== null && r.disk_quota_mb ? r.disk_used_mb / r.disk_quota_mb : -1,
        render: (r) => {
          // No snapshot is not the same fact as 0 MB used — say so.
          if (r.disk_used_mb === null) {
            return <span className="text-xs italic text-muted-foreground">no snapshot</span>;
          }
          // usagePercent + usageTone are the shared thresholds the customer's
          // own usage bars use — the same fullness must not read as "fine" on
          // one screen and "critical" on another.
          const pct = r.disk_quota_mb ? usagePercent(r.disk_used_mb, r.disk_quota_mb) : null;
          const tone =
            pct === null
              ? 'text-foreground'
              : usageTone(pct) === 'danger'
                ? 'text-danger'
                : usageTone(pct) === 'warning'
                  ? 'text-warning-dark'
                  : 'text-foreground';
          return (
            <div className="flex flex-col" title={r.usage_as_of ? `Snapshot of ${r.usage_as_of}` : undefined}>
              <span className={`text-sm tabular-nums ${tone}`}>
                {formatMB(r.disk_used_mb)}
                {r.disk_quota_mb ? ` / ${formatMB(r.disk_quota_mb)}` : ''}
              </span>
              {pct !== null && (
                <span className="text-xs tabular-nums text-muted-foreground">{pct}% of quota</span>
              )}
            </div>
          );
        },
        width: 'w-36',
        hideOnMobile: true,
      },
      {
        key: 'period-end',
        header: 'Period end',
        sortValue: (r) =>
          r.current_period_end ? new Date(r.current_period_end).getTime() : Infinity,
        render: (r) => <span className="text-foreground">{formatDate(r.current_period_end)}</span>,
      },
      {
        // F-12. `dunning_policy` decides whether billing-cron may suspend or
        // terminate this account, and until now it appeared NOWHERE in the
        // portal — the most consequential flag on a hosting row was readable
        // only by writing SQL. It sits beside Flags because that is where a
        // reader already looks for "what is true about this account".
        key: 'dunning',
        header: 'Dunning',
        sortValue: (r) => r.dunning_policy ?? '',
        render: (r) => {
          const meta = dunningPolicyMeta(r.dunning_policy);
          return (
            <span title={meta.detail}>
              <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            </span>
          );
        },
        width: 'w-40',
        hideOnMobile: true,
      },
      {
        // Slice 2 (2026-09-10). Two facts, never conflated: what WHM last
        // said (observed) and what the books want (desired). The badge is
        // the verdict; the tooltip carries both sides and the reason.
        key: 'server',
        header: 'Server vs books',
        sortValue: (r) => (booksVsServerMeta(r.books_vs_server).disagree ? 0 : 1),
        render: (r) => {
          const obs = hostingStateMeta(r.hosting_state);
          const verdict = booksVsServerMeta(r.books_vs_server);
          const title =
            `WHM: ${obs.label}${r.hosting_state_observed_at ? ` (seen ${formatDate(r.hosting_state_observed_at)})` : ''}` +
            ` · books want: ${r.hosting_desired_state ?? '—'} (${desiredReasonLabel(r.hosting_desired_reason_code)})` +
            ` · ${verdict.detail}`;
          return (
            <div className="flex flex-wrap items-center gap-1" title={title}>
              <StatusBadge tone={obs.tone}>{obs.label}</StatusBadge>
              {verdict.disagree && <StatusBadge tone={verdict.tone}>{verdict.label}</StatusBadge>}
              {r.books_vs_server === 'converging' && <StatusBadge tone="info">Converging</StatusBadge>}
            </div>
          );
        },
        width: 'w-48',
        hideOnMobile: true,
      },
      {
        key: 'flags',
        header: 'Flags',
        render: (r) => (
          <div className="flex flex-wrap items-center gap-1">
            {r.scheduled_termination_at && (
              <StatusBadge tone="overdue">Terminating</StatusBadge>
            )}
            {!r.service_plan_name && (
              <StatusBadge tone="expiring">No plan</StatusBadge>
            )}
            {!r.scheduled_termination_at && r.service_plan_name && (
              <StatusBadge tone="active">Active</StatusBadge>
            )}
          </div>
        ),
        width: 'w-32',
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Hosting accounts"
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Hosting' }]}
    >
      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Cross-customer view of every linked cPanel account. <strong>Server vs books</strong> shows what
          WHM last reported (refreshed nightly at 01:30 UTC and on every WHM job) against what the books
          want. Live quotas and IPs still live on the per-customer Hosting tab. Click a row to drill in.
        </p>
      </Card>

      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="server" className="text-sm text-muted-foreground">
              WHM server
            </label>
            <select
              id="server"
              value={serverFilter}
              onChange={(e) => setServerFilter(e.target.value)}
              className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="all">All servers</option>
              {servers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={disagreeOnly}
              onChange={(e) => setDisagreeOnly(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            Disagree only
          </label>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Linked"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search user, domain, customer, package"
            className="ml-auto w-full max-w-xs"
          />
        </div>
      </Card>

      <UniversalTable<AdminAccountRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        caption={loading ? undefined : table.caption}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/hosting`)}
        isLoading={loading}
        error={error}
        empty={{
          icon: Server,
          title: rows.length === 0 ? 'No hosting accounts linked' : 'No matches',
          description:
            rows.length === 0
              ? 'Use Customers → Import existing or New customer to link accounts.'
              : 'Try a different server or search term.',
        }}
      />
    </UniversalPage>
  );
}
